import { ActionIcon, Group, Menu } from "@mantine/core";
import { IconPencil, IconPlus } from "@tabler/icons-react";
import type {
  Control,
  FieldPathByValue,
  FieldPathValue,
  FieldValues,
  UseFormSetValue
} from "react-hook-form";
import { useWatch } from "react-hook-form";
import { FIELD_WIZARD_REGISTRY } from "../../../WizardStack/wizardRegistry";
import type { WizardKey } from "../../../WizardStack/wizardRegistry";
import { useWizardStack } from "../../../WizardStack/WizardStackContext";
import type { FormInputData } from "./FormFieldController";
import { FormFieldController } from "./FormFieldController";

type RelatedRecordId = number | null | undefined;

/**
 * Which wizard the create and edit buttons open. A single `WizardKey` is
 * enough when the select lists one record type. When it mixes types, the field
 * only holds an id and cannot tell which type that id is, so the call site —
 * which owns the list the select was built from — supplies the mapping.
 */
type CreatableTarget =
  | WizardKey
  | {
      /** Types the create button offers, one menu entry each. */
      oneOf: Array<WizardKey>;
      /** Type of the selected id; `undefined` hides the edit button. */
      resolve: (id: number) => WizardKey | undefined;
    };

type CreatableFormFieldControllerProps<
  TFieldValues extends FieldValues,
  TName extends FieldPathByValue<TFieldValues, RelatedRecordId>
> = {
  control: Control<TFieldValues>;
  data: Extract<FormInputData<TFieldValues, TName>, { type: "select" }> & {
    label: string;
  };
  setValue: UseFormSetValue<TFieldValues>;
  target: CreatableTarget;
};

function CreatableFormFieldController<
  TFieldValues extends FieldValues,
  TName extends FieldPathByValue<TFieldValues, RelatedRecordId>
>({
  control,
  data,
  setValue,
  target
}: CreatableFormFieldControllerProps<TFieldValues, TName>) {
  const { openWizard } = useWizardStack();
  const value = useWatch({ control, name: data.name });
  const resolvedWizardKey =
    typeof target === "string"
      ? target
      : value == null
        ? undefined
        : target.resolve(value);
  const createButtonLabel = `${data.label} erstellen`;
  const editButtonLabel = `${data.label} bearbeiten`;
  const actionIconSize = `input-${data.size ?? "sm"}`;

  function handleClickCreate(wizardKey: WizardKey) {
    openWizard(wizardKey, {
      mode: "create",
      onSuccess: (object) => {
        // shouldValidate clears any stale server-side error on this field.
        // TName is constrained to id-valued paths, but react-hook-form can't
        // narrow `FieldPathValue` through that constraint.
        setValue(data.name, object.id as FieldPathValue<TFieldValues, TName>, {
          shouldDirty: true,
          shouldValidate: true
        });
      }
    });
  }

  function handleClickEdit() {
    if (!resolvedWizardKey) {
      return;
    }

    openWizard(resolvedWizardKey, { mode: "edit", objectId: value });
  }

  const dataWithCreateButton: FormInputData<TFieldValues, TName> = {
    ...data,
    action: (
      <Group gap="xs" wrap="nowrap">
        {value != null && resolvedWizardKey && (
          <ActionIcon
            aria-label={editButtonLabel}
            size={actionIconSize}
            variant="outline"
            onClick={handleClickEdit}
          >
            <IconPencil size={16} />
          </ActionIcon>
        )}
        {typeof target === "string" ? (
          <ActionIcon
            aria-label={createButtonLabel}
            size={actionIconSize}
            variant="outline"
            onClick={() => handleClickCreate(target)}
          >
            <IconPlus size={16} />
          </ActionIcon>
        ) : (
          <Menu>
            <Menu.Target>
              <ActionIcon
                aria-label={createButtonLabel}
                size={actionIconSize}
                variant="outline"
              >
                <IconPlus size={16} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              {target.oneOf.map((wizardKey) => (
                <Menu.Item
                  key={wizardKey}
                  onClick={() => handleClickCreate(wizardKey)}
                >
                  {FIELD_WIZARD_REGISTRY[wizardKey].displayName}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
        )}
      </Group>
    )
  };

  return <FormFieldController control={control} data={dataWithCreateButton} />;
}

export { CreatableFormFieldController, CreatableFormFieldControllerProps };
export type { CreatableTarget };
